import type {
	CloudinaryUploadResult,
	NormalizedFile,
} from 'components/FileUploader/fileUploadUtils'

/**
 * What the user wants to do with the note question:
 *  - 'none'      upload the images, write no note
 *  - 'single'    one note holding every image
 *  - 'per-image' one note per image, each holding exactly that image
 */
export type NotePlan = 'none' | 'single' | 'per-image'

export type StagedImageStatus = 'pending' | 'uploading' | 'uploaded' | 'error'

/**
 * A file the user has picked but not yet committed. Held locally — nothing
 * reaches Cloudinary or Mongo until Confirm.
 */
export interface StagedImage {
	/** Local uid, not a Cloudinary public_id. */
	id: string
	file: File
	/** URL.createObjectURL(file); must be revoked on remove and on unmount. */
	preview: string
	/** Empty unless the plan is 'per-image'. */
	noteHtml: string
	status: StagedImageStatus
	errorMessage?: string
	upload?: CloudinaryUploadResult
	/** True once this image's DB rows exist, so a retry can skip it. */
	committed?: boolean
}

/** A staged image that uploaded successfully, ready to be written. */
export interface CommitImage {
	id: string
	file: File
	/** Raw Cloudinary response — carries public_id, bytes, format. */
	upload: CloudinaryUploadResult
	normalized: NormalizedFile
	/** Empty unless the plan is 'per-image'. */
	noteHtml: string
}

/**
 * Tracks what a commit has already written, so a retry after a mid-commit
 * failure re-runs only the missing steps. The wizard keeps one of these across
 * attempts and hands it to onCommit.
 */
export interface CommitState {
	/** Images whose gallery row exists. */
	committedImageIds: Set<string>
	/**
	 * Images whose own note row exists (plan === 'per-image'). Tracked apart
	 * from committedImageIds because the note is written first: if the note
	 * succeeds and the image row then fails, a retry must skip the note or it
	 * would write a duplicate.
	 */
	committedNoteImageIds: Set<string>
	/** The one shared note exists (plan === 'single'). */
	sharedNoteCommitted: boolean
}

export interface ImageUploadCommit {
	plan: NotePlan
	/** Successful uploads only. */
	images: CommitImage[]
	/** Empty unless the plan is 'single'. */
	sharedNoteHtml: string
}

/**
 * The only entity-specific thing the wizard knows about. Projects, portfolios
 * and sites each supply one of these; the wizard itself stays generic.
 */
export interface ImageUploadTarget {
	/** Used in the dialog copy, e.g. 'Add images to Riverside Plaza'. */
	entityLabel: string
	/** Cloudinary folder, e.g. 'mdt/sites/<id>'. */
	folder?: string
	/**
	 * Writes the rows. `state` carries what a previous failed attempt already
	 * wrote; pass it straight through to commitImageUpload so a retry does not
	 * duplicate anything.
	 */
	onCommit: (commit: ImageUploadCommit, state: CommitState) => Promise<unknown>
}

export interface ImageUploadWizardProps {
	open: boolean
	onClose: () => void
	target: ImageUploadTarget
	/** Lets a caller that already ran a native picker skip the dropzone step. */
	initialFiles?: File[]
	/** Defaults to MAX_FILES. Each image costs up to 2 round-trips on commit. */
	maxFiles?: number
}

/**
 * Per-image commit cost is up to two sequential writes, so this cap keeps a
 * single confirm bounded. Raising it should wait for a batch endpoint.
 */
export const MAX_FILES = 10
